import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useIsIframeEmbed } from "@circle-react/hooks/useIsIframeEmbed";
import type {
  ContentReactionCounts,
  ContentReactionType,
} from "@circle-react/types/ContentReaction";
import { CONTENT_REACTION_TYPES } from "@circle-react/types/ContentReaction";
import type { SentimentProps } from "@circleco/compass/components/Sentiment";
import { SentimentPicker } from "@circleco/compass/components/SentimentPicker";
import { Typography } from "@circleco/compass/components/Typography";
import { useState } from "react";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { userLikeApi } from "@/react/api/userLikeApi";
import type { UserLikableType } from "@/react/api/userLikeApi";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { AnimatedCount } from "@/react/components/shared/AnimatedCount";
import { userPaths } from "@/react/helpers/urlHelpers";

import {
  DEFAULT_REACTION_TYPE,
  REACTION_PRESENTATION,
  SENTIMENT_TO_REACTION_TYPE,
} from "./constants";
import { updateReactionCounts } from "./helpers";
import { ReactionSummary } from "./ReactionSummary";
import type {
  PostCommentReactionableType,
  ReactionStateUpdates,
} from "./types";

interface PostCommentReactionActionProps {
  canReact: boolean;
  className?: string;
  currentUserReaction?: ContentReactionType | null;
  iconOnly?: boolean;
  isLiked: boolean;
  likesCount: number;
  loginNotice: string;
  onChange?: (updates: ReactionStateUpdates) => void;
  onSettled?: () => void;
  onSummaryClick?: () => void;
  reactionableId: number;
  reactionableType: PostCommentReactionableType;
  reactionCounts: ContentReactionCounts;
  showSummary?: boolean;
  variant?: "compact" | "default";
}

interface ReactionState {
  counts: ContentReactionCounts;
  likesCount: number;
  reaction: ContentReactionType | null;
}

interface AddReactionVariables {
  previousState: ReactionState;
  reactionType: ContentReactionType;
}

interface RemoveReactionVariables {
  previousState: ReactionState;
}

const getReactionStateKey = ({ counts, likesCount, reaction }: ReactionState) =>
  [
    reaction ?? "none",
    likesCount,
    ...CONTENT_REACTION_TYPES.map(reactionType => counts[reactionType]),
  ].join(":");

export const PostCommentReactionAction = ({
  canReact,
  className,
  currentUserReaction = null,
  iconOnly = false,
  isLiked,
  likesCount,
  loginNotice,
  onChange,
  onSettled,
  onSummaryClick,
  reactionCounts,
  reactionableId,
  reactionableType,
  showSummary = true,
  variant = "default",
}: PostCommentReactionActionProps) => {
  const { currentUser, isViewOnlyMasquerading } = usePunditUserContext();
  const isIframeEmbed = useIsIframeEmbed();
  const { error } = useToast();
  const serverState: ReactionState = {
    counts: reactionCounts,
    likesCount,
    // Preserve the legacy binary-like state until reaction_summary is available.
    reaction: currentUserReaction ?? (isLiked ? DEFAULT_REACTION_TYPE : null),
  };
  const serverStateKey = getReactionStateKey(serverState);
  const [lastServerStateKey, setLastServerStateKey] = useState(serverStateKey);
  const [reactionState, setReactionState] = useState(serverState);

  if (lastServerStateKey !== serverStateKey) {
    setLastServerStateKey(serverStateKey);
    setReactionState(serverState);
  }

  const {
    counts: reactionCountsState,
    likesCount: likesCountState,
    reaction: selectedReaction,
  } = reactionState;

  const userLikableType: UserLikableType =
    reactionableType === "post" ? "Post" : "Comment";
  const requestOptions = {
    forReactQuery: true,
    userLikeableId: reactionableId,
    userLikableType,
  };

  const redirectToLogin = () => {
    error(loginNotice);

    const loginPath = userPaths.signIn({
      params: { post_login_redirect: window.location.href },
    });

    if (isIframeEmbed) {
      window.open(loginPath, "_blank", "noreferrer");
    } else {
      window.location.href = loginPath;
    }
  };

  const publishState = (nextState: ReactionState) => {
    const isNextLiked = Boolean(nextState.reaction);

    setReactionState(nextState);
    onChange?.({
      is_liked: isNextLiked,
      is_liked_by_current_user: isNextLiked,
      likes_count: nextState.likesCount,
      reaction_summary: {
        counts: nextState.counts,
        current_user_reaction_type: nextState.reaction,
      },
      user_likes_count: nextState.likesCount,
    });
  };

  const applyState = ({
    nextReaction,
    nextLikesCount,
  }: {
    nextLikesCount: number;
    nextReaction: ContentReactionType | null;
  }) =>
    publishState({
      counts: updateReactionCounts({
        counts: reactionCountsState,
        previousReaction: selectedReaction,
        nextReaction,
      }),
      likesCount: nextLikesCount,
      reaction: nextReaction,
    });

  const { mutate: addReaction, isPending: isAddingReaction } = useMutation<
    unknown,
    unknown,
    AddReactionVariables
  >({
    mutationFn: ({ reactionType }) =>
      userLikeApi.create({ ...requestOptions, reactionType }),
    onError: (_mutationError, { previousState }) => {
      publishState(previousState);
      error(t("reactions.errors.create_failure"));
    },
    onSettled: () => onSettled?.(),
  });

  const { mutate: removeReaction, isPending: isRemovingReaction } = useMutation<
    unknown,
    unknown,
    RemoveReactionVariables
  >({
    mutationFn: () => userLikeApi.destroy(requestOptions),
    onError: (_mutationError, { previousState }) => {
      publishState(previousState);
      error(t("reactions.errors.destroy_failure"));
    },
    onSettled: () => onSettled?.(),
  });

  const isLoading = isAddingReaction || isRemovingReaction;

  const handleReaction = (reactionType: ContentReactionType) => {
    if (!currentUser) {
      redirectToLogin();
      return;
    }

    if (isViewOnlyMasquerading || !canReact || isLoading) {
      return;
    }

    if (selectedReaction === reactionType) {
      const nextLikesCount = Math.max(likesCountState - 1, 0);

      applyState({
        nextReaction: null,
        nextLikesCount,
      });
      removeReaction({ previousState: reactionState });
      return;
    }

    const nextLikesCount = selectedReaction
      ? likesCountState
      : likesCountState + 1;

    applyState({
      nextReaction: reactionType,
      nextLikesCount,
    });
    addReaction({ previousState: reactionState, reactionType });
  };

  const isReactionDisabled = isViewOnlyMasquerading || !canReact || isLoading;
  const selectedSentiment = selectedReaction
    ? REACTION_PRESENTATION[selectedReaction].sentiment
    : undefined;
  const handleSentimentSelect = (sentiment?: SentimentProps["kind"]) => {
    const reactionType = sentiment
      ? SENTIMENT_TO_REACTION_TYPE[sentiment]
      : selectedReaction;

    // Compass re-emits the active overlay sentiment; only an undefined trigger
    // event removes the reaction.
    if (reactionType && sentiment !== selectedSentiment) {
      handleReaction(reactionType);
    }
  };

  return (
    <div
      className={classNames("flex items-center gap-2", className)}
      data-testid={`reaction-action-${reactionableType}`}
    >
      {showSummary && variant === "default" && (
        <ReactionSummary
          count={likesCountState}
          counts={reactionCountsState}
          onClick={onSummaryClick}
        />
      )}
      <div
        className={classNames(
          "inline-flex",
          variant === "compact" && "items-center gap-2",
        )}
      >
        <SentimentPicker
          data-testid="reaction-picker"
          disabled={isReactionDisabled}
          iconOnly={iconOnly || variant === "compact"}
          value={selectedSentiment}
          onSentimentSelect={handleSentimentSelect}
          i18n={{
            like: t(REACTION_PRESENTATION.like.labelKey),
            celebrate: t(REACTION_PRESENTATION.celebrate.labelKey),
            support: t(REACTION_PRESENTATION.support.labelKey),
            love: t(REACTION_PRESENTATION.love.labelKey),
            idea: t(REACTION_PRESENTATION.insightful.labelKey),
            funny: t(REACTION_PRESENTATION.funny.labelKey),
          }}
        />
        {variant === "compact" && (
          <Typography
            variant="label-md"
            color="secondary"
            component="span"
            className={classNames(
              selectedReaction &&
                REACTION_PRESENTATION[selectedReaction].selectedClassName,
            )}
          >
            <AnimatedCount count={likesCountState} />
          </Typography>
        )}
      </div>
    </div>
  );
};
